/* eslint-disable max-lines */
import {
  getAIDefects,
  getAIDefectsLabels,
  getAiDetectionRegions,
  getGTDefects,
  getGTDetectionRegions,
} from 'app/utils/helpers';
import { keyBy } from 'lodash';
import { createSelector } from 'reselect';
import { setIn } from 'seamless-immutable';

import { STORE_KEYS } from '../storeKeys';

const createAnnotatorInput = (fileSet, regions = []) => {
  const { id, src, name } = fileSet;
  return [
    {
      id,
      src,
      name,
      regions,
    },
  ];
};

const isRegionsSame = (region1, region2) => {
  const { x: x1, y: y1, w: w1, h: h1 } = region1;
  const { x: x2, y: y2, w: w2, h: h2 } = region2;

  return x1 === x2 && y1 === y2 && w1 === w2 && h1 === h2;
};

const getConbinedDetectionData = (aiDetections = [], gtDetections = []) => {
  let updatedAiDetections = aiDetections;
  const updatedGTDetections = gtDetections.map(item => {
    let newGTTags = item;
    updatedAiDetections.forEach((aiItem, index) => {
      if (isRegionsSame(aiItem, item) && aiItem.visible) {
        // combine regions here and hide ai detection
        updatedAiDetections = setIn(updatedAiDetections, [index], {
          ...aiItem,
          visible: false,
        });

        newGTTags = {
          ...item,
          tags: [
            ...aiItem.tags.map(aiTags => ({ ...aiTags, is_ai_tag: true })),
            ...item.tags,
          ],
        };
      }
    });
    return newGTTags;
  });

  return [...updatedAiDetections, ...updatedGTDetections];
};

const selectReview = state => state[STORE_KEYS.REVIEW_DATA];

export const selectReviewData = createSelector(
  [selectReview],
  review => review.data
);

export const selectFetchingReviewData = createSelector(
  [selectReview],
  review => review.fetchingReviewData
);

export const selectFetchingReviewDataBackground = createSelector(
  [selectReview],
  review => review.fetchingReviewData && review.isBackgroundUpdate
);

export const selectFileSetCount = createSelector(
  [selectReview],
  review => review.fileSetCount
);

export const selectReviewDataNext = createSelector(
  [selectReview],
  review => review.next
);

export const selectGridModes = createSelector(
  [selectReview],
  review => review.gridModes
);

export const selectActiveGridMode = createSelector(
  [selectReview],
  review => review.activeGridMode
);

export const selectImageModes = createSelector(
  [selectReview],
  review => review.imageModes
);

export const selectActiveImageMode = createSelector(
  [selectReview],
  review => review.activeImageMode
);

export const selectSearchText = createSelector(
  [selectReview],
  review => review.searchText
);

export const selectActiveImg = createSelector(
  [selectReview],
  review => review.activeImg
);

export const selectFileSetId = createSelector(
  [selectActiveImg, selectReviewData],
  (activeImg, reviewData) => reviewData?.[activeImg[0]]?.fileSetId
);

export const selectFileSetDefects = createSelector(
  [selectReview],
  review => review.fileSetDefects
);

export const selectIsFileSetDefectsLoading = createSelector(
  [selectReview],
  review => review.isFileSetDefectsLoading
);

export const selectSelectedTool = createSelector(
  [selectReview],
  review => review.selectedTool
);

export const selectUseAiAssistance = createSelector(
  [selectReview],
  review => review.useAIAssistance
);

export const selectAppliedForAllModelId = createSelector(
  [selectReview],
  review => review.appliedForAllModelId
);

export const selectIsModelAppliedForAll = createSelector(
  [selectAppliedForAllModelId],
  appliedModelId => !!appliedModelId
);

export const selectModelVisibilityObj = createSelector(
  [selectReview],
  review => review.modelVisibilityObj
);

export const selectSelectAll = createSelector(
  [selectReview],
  review => review.selectAll
);

export const selectModelDetectionList = createSelector(
  [selectReview],
  review => review.modelDetectionList
);

export const selectUserDetectionList = createSelector(
  [selectReview],
  review => review.userDetectionList
);

export const selectOtherDefects = createSelector(
  [selectReview],
  review => review.otherDefects
);

export const selectUserClassification = createSelector(
  [selectReview],
  review => review.userClassification
);

export const selectActiveFileSet = createSelector(
  [selectReviewData, selectActiveImg],
  (data, activeImg) =>
    data[activeImg.length ? activeImg[activeImg.length - 1] : 0] ?? {}
);

export const selectFirstActiveFileSet = createSelector(
  [selectReviewData, selectActiveImg],
  (data, activeImg) => data[activeImg.length ? activeImg[0] : 0] ?? {}
);

export const selectContainerMeta = createSelector(
  [selectReview],
  review => review.containerMeta
);

export const selectFirstFileSetDefects = createSelector(
  [selectFileSetDefects, selectReviewData],
  (fileSetDefects, fileSets) => fileSetDefects[fileSets[0]?.id] ?? {}
);

export const selectImageSizes = createSelector(
  [selectReview],
  review => review.imageSizes
);

export const selectActiveFileSetDefects = createSelector(
  [selectFileSetDefects, selectActiveFileSet],
  (fileSetDefects, fileSets) => fileSetDefects[fileSets.id] ?? {}
);

export const selectActiveImageModeCount = createSelector(
  [selectReview, selectActiveImageMode],
  (review, activeMode) => {
    const active = keyBy(review.imageModes, 'label')[activeMode];
    return active?.subLabel || 0;
  }
);

export const selectAiClassification = createSelector(
  [selectActiveFileSetDefects],
  fileSetDefect => getAIDefects(fileSetDefect, 'CLASSIFICATION')
);

export const selectGTClassification = createSelector(
  [selectActiveFileSetDefects],
  fileSetDefect => getGTDefects(fileSetDefect, 'CLASSIFICATION')
);

export const selectIsNoDefectClassification = createSelector(
  [selectActiveFileSet, selectReview],
  (fileSet, { fileSetDefects }) => {
    return (
      fileSetDefects[fileSet.id]?.['gt_classifications']?.['is_no_defect'] ||
      false
    );
  }
);

export const selectFileSetByIndex = index =>
  createSelector([selectReviewData], reviewData => reviewData[index]);

export const selectIsSelectedByIndex = index =>
  createSelector(
    [selectActiveImg, selectSelectAll],
    (activeImg, selectAll) => selectAll || activeImg.includes(index)
  );

export const selectFileSetDefectsById = id =>
  createSelector([selectFileSetDefects], fileSetDefects => fileSetDefects[id]);

export const selectGTLabelsByFileSetDefectsId = (id, useCaseType) =>
  createSelector([selectFileSetDefects], fileSetDefects => {
    if (useCaseType === 'CLASSIFICATION') {
      return getGTDefects(fileSetDefects[id], useCaseType).map(d => d.name);
    }

    return getGTDefects(fileSetDefects[id], useCaseType);
  });

export const selectLocalGtLabelsByFileSetDefectsId = id =>
  createSelector([selectFileSetLocalDefects], fileSetLocalDefects => {
    const value = fileSetLocalDefects[id];
    return value ? (value.name ? [value.name] : []) : null;
  });

export const selectAIDefectsByFileSetDefectsId = (id, useCaseType) =>
  createSelector([selectFileSetDefects], fileSetDefects =>
    getAIDefects(fileSetDefects[id], useCaseType)
  );

export const selectIsMultiSelected = createSelector(
  [selectActiveImg, selectReviewData, selectSelectAll],
  (activeImg, reviewData, selectAll) =>
    (reviewData.length > 1 && selectAll) || activeImg.length > 1
);

export const selectFileSetDefectsLength = createSelector(
  [selectFileSetDefects],
  fileSetDefects => Object.keys(fileSetDefects).length
);

export const selectActiveFileSetUseCaseId = createSelector(
  [selectFirstActiveFileSet],
  activeFileSet => activeFileSet.use_case
);

export const selectActiveFileSetUploadSessionIds = createSelector(
  [selectReviewData, selectActiveImg],
  (data, activeImg) =>
    data?.length && activeImg.length
      ? activeImg?.map(i => data[i]?.upload_session)
      : []
);

export const selectIsNoDefect = createSelector(
  [selectActiveFileSet, selectReview],
  (fileSet, { fileSetDefects }) =>
    fileSetDefects[fileSet.id]?.['gt_detections']?.['is_no_defect'] || false
);

export const selectIsNoDefectAI = createSelector(
  [selectActiveFileSet, selectReview],
  (fileSet, { fileSetDefects }) =>
    fileSetDefects[fileSet.id]?.['model_detections']?.[0]?.['is_no_defect'] ||
    false
);

export const selectDetectionRegions = createSelector(
  [selectActiveFileSet, selectReview],
  (fileSet, { fileSetDefects }) =>
    fileSetDefects[fileSet.id]?.['gt_detections']?.['detection_regions']
);

export const selectGTDetection = createSelector(
  [selectActiveFileSet, selectReview],
  (fileSet, { fileSetDefects }) =>
    fileSetDefects[fileSet.id]?.['gt_detections']?.['id']
);

export const selectAiDetections = createSelector(
  [selectActiveFileSetDefects, selectIsNoDefectAI],
  (fileSetDefects, isNoDefect) =>
    getAiDetectionRegions({ fileSetDefects, isNoDefect })
);

export const selectGTDetections = createSelector(
  [selectActiveFileSetDefects, selectIsNoDefect],
  (fileSetDefects, isNoDefect) =>
    getGTDetectionRegions({ fileSetDefects, isNoDefect })
);

export const selectIsUserClassificationLoading = createSelector(
  [selectReview],
  review => review.isUserClassificationLoading
);

export const selectIsBulkClassificationUpdating = createSelector(
  [selectReview],
  review => review.isBulkClassificationUpdating
);

export const selectActiveFilesets = createSelector(
  [selectActiveImg, selectReviewData],
  (activeImg, data) =>
    data.length ? activeImg.map(item => data[item]?.files || {}).flat() : []
);

export const selectActiveFilesetsDots = createSelector(
  [selectActiveImg, selectReviewData],
  (activeImg, data) =>
    data.length ? activeImg.map(item => data[item] || {}).flat() : []
);

export const selectCommonFileSetDefectId = useCaseType =>
  createSelector(
    [selectActiveFilesets, selectFileSetDefects, selectSelectAll],
    (activeFileSets, fileSetDefects, selectAll) => {
      if (selectAll) {
        return null;
      }

      const activeFileSetDefects = [
        ...new Set(
          activeFileSets
            .map(item => getGTDefects(fileSetDefects[item.id], useCaseType))
            .flat()
        ),
      ];

      if (
        activeFileSets.length === activeFileSetDefects.length && // Handling Empty label
        [...new Set(activeFileSetDefects.map(item => item.id))].length === 1
      ) {
        return activeFileSetDefects[0].id;
      }

      return null;
    }
  );

export const selectIsActiveFilesInferenced = useCaseType =>
  createSelector(
    [selectActiveFilesets, selectFileSetDefects],
    (activeFileSets, fileSetDefects) => {
      for (const activeFileSet of activeFileSets) {
        const aiDefects = getAIDefectsLabels(
          fileSetDefects[activeFileSet.id],
          useCaseType
        );

        if (!aiDefects.length) {
          return false;
        }
      }

      return true;
    }
  );

export const selectWaferId = createSelector(
  [selectActiveFileSet],
  activeFileSet => activeFileSet.wafer
);

export const selectSelectedWaferDots = createSelector(
  [selectReview],
  review => review.selectedWaferDots
);

export const selectSelectedClusterRectangle = createSelector(
  [selectReview],
  review => review.selectedClusterRectangle
);

export const selectIsUploadSessionLive = createSelector(
  [selectReview],
  review => review.isLive
);

export const selectTrainingSessionId = mlModelId =>
  createSelector(
    [selectReview],
    review => review.modelsDict?.[mlModelId]?.training_session
  );

export const selectIsFileSetDeleting = createSelector(
  [selectReview],
  review => review.isFileSetDeleting
);

export const selectDefectColorCodes = createSelector(
  [selectReview],
  review => review.defectColorCodes
);

export const selectIsCommonModelForSelectedFiles = usecaseType =>
  createSelector(
    [
      selectAppliedForAllModelId,
      selectSelectAll,
      selectActiveFilesets,
      selectFileSetDefects,
    ],
    (modelAppliedForAll, selectAll, activeFileSets, fileSetDefects) => {
      if (modelAppliedForAll) {
        return {
          isCommon: true,
          mlModelId: modelAppliedForAll,
        };
      }

      if (selectAll) {
        return {
          isCommon: false,
          mlModelId: null,
        };
      }

      const useCaseTypeKey =
        usecaseType === 'CLASSIFICATION'
          ? 'model_classifications'
          : 'model_detections';

      const activeModelIds = activeFileSets.reduce((prev, curr) => {
        const mlModelId =
          fileSetDefects[curr.id]?.[useCaseTypeKey]?.[0]?.ml_model;
        if (mlModelId && !prev.includes(mlModelId)) {
          return [...prev, mlModelId];
        }

        return prev;
      }, []);

      return {
        isCommon: activeModelIds.length === 1,
        mlModelId: activeModelIds.length === 1 ? activeModelIds[0] : null,
      };
    }
  );

export const selectIsActiveFileSetInferenced = usecaseType =>
  createSelector(
    [selectActiveFilesets, selectFileSetDefects],
    (activeFileSets, fileSetDefects) => {
      const useCaseTypeKey =
        usecaseType === 'CLASSIFICATION'
          ? 'model_classifications'
          : 'model_detections';

      return !activeFileSets.some(item => {
        return !(fileSetDefects[item.id]?.[useCaseTypeKey] || []).length;
      });
    }
  );

// NOTE: This selector will not work if in future review screen will
// have ability to open images from multiple usecases
export const selectFolderUsecase = createSelector(
  [selectActiveFileSet],
  fileset => fileset?.use_case || null
);

export const selectModelDict = createSelector(
  [selectReview],
  review => review.modelsDict
);

export const selectCurrentModelId = createSelector(
  [selectAppliedForAllModelId, selectAiClassification, selectAiDetections],
  (appliedForAllModelId, modelDefects, detectionModelDefects) => {
    const modelId =
      parseInt(modelDefects[0]?.ml_model_id, 10) ||
      parseInt(Object.keys(detectionModelDefects)[0], 10);
    return appliedForAllModelId || modelId || undefined;
  }
);

export const selectActiveModelId = usecaseType =>
  createSelector(
    [selectModelDict, selectIsCommonModelForSelectedFiles(usecaseType)],
    (modelsDict, isCommonModel) => {
      const { mlModelId } = isCommonModel;
      return mlModelId;
    }
  );

export const selectActiveModel = usecaseType =>
  createSelector(
    [selectActiveModelId(usecaseType), selectModelDict],
    (mlModelId, modelsDict) => {
      return modelsDict[mlModelId];
    }
  );

export const selectTransferableImageCount = createSelector(
  [selectReview],
  review => review.transferableImageIndexes.size
);
export const selectTransferableImageIndexes = createSelector(
  [selectReview],
  review => review.transferableImageIndexes
);

export const selectFileSetLocalDefects = createSelector(
  [selectReview],
  review => review.fileSetLocalDefects
);

export const selectActiveFileSetLocalDefects = createSelector(
  [selectFileSetLocalDefects, selectActiveFileSet],
  (fileSetLocalDefects, activeFileSet) => fileSetLocalDefects[activeFileSet.id]
);

export const selectFileSetLocalDefectsSavingStatus = createSelector(
  [selectReview],
  review => review.fileSetLocalDefectsSavingStatus
);

export const selectMarkImageCenter = createSelector(
  [selectReview],
  review => review.markImageCenter
);

export const selectActiveFileSetDetections = createSelector(
  [
    selectGTDetections,
    selectAiDetections,
    selectCurrentModelId,
    selectModelVisibilityObj,
    selectUseAiAssistance,
  ],
  (gtDetections, aiDetections, modelId, modelOutputList, useAIAssistance) => {
    if (modelOutputList[modelId] && useAIAssistance) {
      return getConbinedDetectionData(
        aiDetections?.[modelId]?.detection_regions,
        gtDetections?.detection_regions
      );
    }
    return [...(gtDetections?.detection_regions || [])];
  }
);

export const selectAnnotatorInput = createSelector(
  [selectActiveFileSet, selectActiveFileSetDetections],
  (fileSet, regions) => createAnnotatorInput(fileSet, regions)
);

export const selectReviewIrr = createSelector(
  [selectReview],
  review => review.irr
);

export const selectReviewIrrView = createSelector(
  [selectReviewIrr],
  irr => irr.showIRRview
);
export const selectReviewIrrData = createSelector(
  [selectReviewIrr],
  irr => irr.data
);

export const selectFileSetTags = createSelector(
  [selectReview],
  review => review.fileSetTags
);

export const selectFileSetTagUpdates = createSelector(
  [selectReview],
  review => review.fileSetTagUpdates
);

export const selectUseCaseId = createSelector(
  [selectReview],
  review => review.useCaseId
);
